/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  AfterContentInit,
  Component,
  ContentChildren,
  DestroyRef,
  inject,
  input,
  output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icon/icon.component';
import { SubFormListItemComponent } from './sub-form-list-item/sub-form-list-item.component';

@Component({
  selector: 'ods-sub-form-list',
  imports: [ButtonComponent, IconComponent],
  host: { class: 'block rounded-md border border-grayborder p-4' },
  template: `
    @if (heading()) {
      <div
        role="heading"
        [attr.aria-level]="headingLevel()"
        class="text-text mb-4 font-medium"
        [class.text-2xl]="headingLevel() === 2"
        [class.text-xl]="headingLevel() === 3"
      >
        {{ heading() }}
      </div>
    }
    <div class="flex flex-col gap-4 empty:hidden">
      <ng-content />
    </div>
    <ods-button
      #addButton
      variant="outline"
      [text]="addButtonLabel()"
      (clicked)="onAddButtonClicked()"
      [attr.data-test-id]="'add-' + dataTestId() + '-button'"
      class="mt-4"
    >
      <ods-icon icon name="plus" fill="primary" />
    </ods-button>
  `,
})
export class SubFormListComponent implements AfterContentInit {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public readonly heading = input<string>();
  public readonly headingLevel = input<2 | 3>(2);
  public readonly addButtonLabel = input.required<string>();
  public readonly dataTestId = input<string>('');

  public readonly addButtonClicked = output<void>();

  @ContentChildren(SubFormListItemComponent) items: QueryList<SubFormListItemComponent>;
  @ViewChild('addButton') addButtonComponent: ButtonComponent;

  previousItems: SubFormListItemComponent[] = [];

  ngAfterContentInit(): void {
    this.previousItems = this.items.toArray();
    this.items.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this._focusAfterItemsChanged());
  }

  public onAddButtonClicked(): void {
    this.addButtonClicked.emit();
    setTimeout(() => this.items.last.focusFirstField());
  }

  _focusAfterItemsChanged(): void {
    const currentItems: SubFormListItemComponent[] = this.items.toArray();
    if (currentItems.length < this.previousItems.length) {
      this._focusNeighborOf(currentItems);
    }
    this.previousItems = currentItems;
  }

  _focusNeighborOf(currentItems: SubFormListItemComponent[]): void {
    if (currentItems.length === 0) {
      this.addButtonComponent.focus();
      return;
    }
    const removedIndex: number = this._findRemovedIndex(currentItems);
    currentItems[Math.min(removedIndex, currentItems.length - 1)].focus();
  }

  _findRemovedIndex(currentItems: SubFormListItemComponent[]): number {
    const divergingIndex: number = this.previousItems.findIndex(
      (item, index) => index < currentItems.length && item !== currentItems[index],
    );
    return divergingIndex === -1 ? currentItems.length - 1 : divergingIndex;
  }
}
